import { useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Megaphone, Loader2, Save, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminSponsoredSettings — lets admin define price + discount %  + badge +
 * title/subtitle/bullets for the public "Sponzorirani članak" CTA widget.
 */
export default function AdminSponsoredSettings() {
  const { authFetch } = useAdmin();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    const r = await authFetch(`${API}/admin/sponsored-settings`);
    if (r.ok) setForm(await r.json());
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function update(field, value) { setForm(f => ({ ...f, [field]: value })); }
  function setBullet(i, v) { setForm(f => ({ ...f, bullets: f.bullets.map((b, k) => k === i ? v : b) })); }
  function addBullet() { setForm(f => ({ ...f, bullets: [...(f.bullets || []), ''] })); }
  function removeBullet(i) { setForm(f => ({ ...f, bullets: f.bullets.filter((_, k) => k !== i) })); }

  async function save() {
    setSaving(true);
    const r = await authFetch(`${API}/admin/sponsored-settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        enabled: !!form.enabled,
        price_eur: parseFloat(form.price_eur) || 0,
        discount_pct: Math.max(0, Math.min(100, parseFloat(form.discount_pct) || 0)),
        badge_label: form.badge_label || '',
        title: form.title || '',
        subtitle: form.subtitle || '',
        bullets: (form.bullets || []).filter(b => b.trim()),
      }),
    });
    if (r.ok) { toast.success('Spremljeno'); load(); } else toast.error('Greška');
    setSaving(false);
  }

  if (!form) return <div className="p-10"><Loader2 className="animate-spin text-zinc-500" /></div>;

  const effectivePrice = (parseFloat(form.price_eur) || 0) * (1 - (parseFloat(form.discount_pct) || 0) / 100);

  return (
    <div className="p-4 md:p-6 max-w-3xl" data-testid="admin-sponsored-settings">
      <div className="flex items-center gap-3 mb-5">
        <Megaphone className="text-red-500" size={24} />
        <div>
          <h1 className="text-2xl font-bold">Sponzorirani članak</h1>
          <p className="text-sm text-zinc-400">Postavi cijenu i promotivni popust za self-serve kupnju sponzoriranog članka.</p>
        </div>
      </div>

      <div className="space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.enabled} onChange={e => update('enabled', e.target.checked)} className="accent-red-600" data-testid="sp-enabled" />
          Prikaži promociju sponzoriranog članka na stranici
        </label>
        <div className="grid md:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs text-zinc-400">Cijena (EUR)</span>
            <input type="number" step={1} value={form.price_eur || 0} onChange={e => update('price_eur', e.target.value)}
                   className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="sp-price" />
          </label>
          <label className="block">
            <span className="text-xs text-zinc-400">Popust (%)</span>
            <input type="number" min={0} max={100} step={1} value={form.discount_pct || 0} onChange={e => update('discount_pct', e.target.value)}
                   className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="sp-discount" />
          </label>
          <label className="block">
            <span className="text-xs text-zinc-400">Naljepnica popusta</span>
            <input type="text" value={form.badge_label || ''} onChange={e => update('badge_label', e.target.value)} placeholder="AKCIJA"
                   className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="sp-badge" />
          </label>
        </div>
        <p className="text-sm text-emerald-400">Krajnja cijena: € {effectivePrice.toFixed(2)} {form.discount_pct > 0 ? `(popust -${form.discount_pct}%)` : ''}</p>

        <label className="block">
          <span className="text-xs text-zinc-400">Naslov promocije</span>
          <input type="text" value={form.title || ''} onChange={e => update('title', e.target.value)}
                 className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="sp-title" />
        </label>
        <label className="block">
          <span className="text-xs text-zinc-400">Podnaslov</span>
          <textarea value={form.subtitle || ''} onChange={e => update('subtitle', e.target.value)} rows={2}
                    className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="sp-subtitle" />
        </label>

        <div>
          <p className="text-xs text-zinc-400 mb-2">Ključne točke (prikazane u widgetu)</p>
          <div className="space-y-2">
            {(form.bullets || []).map((b, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input type="text" value={b} onChange={e => setBullet(i, e.target.value)}
                       className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid={`sp-bullet-${i}`} />
                <button onClick={() => removeBullet(i)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
          <button onClick={addBullet} className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-sm rounded">
            <Plus size={14} /> Dodaj točku
          </button>
        </div>

        <div className="pt-2">
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded font-medium disabled:opacity-50" data-testid="sp-save">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Spremi
          </button>
        </div>
      </div>
    </div>
  );
}
